import { Provider } from "../../order";
import { Schema } from "../type-system/schema";
import { NumberComparisonOperator } from "./number-expression";
import { StringMatchOperator } from "./text-expression";

/**
 * Operators for checkout context predicates (collection/product/tag matching).
 * These determine how to match cart products against the target criteria.
 */
export const checkoutContextOperators = [
  "includesAny",
  "includesAll",
  "includesNone",
] as const;

export type CheckoutContextOperator = (typeof checkoutContextOperators)[number];

export const checkoutContextOperatorToLabel: Record<
  CheckoutContextOperator,
  string
> = {
  includesAny: "any product is in",
  includesAll: "all products are in",
  includesNone: "no products are in",
};

export const checkoutContextOperatorToLabelForTags: Record<
  CheckoutContextOperator,
  string
> = {
  includesAny: "any product has tag",
  includesAll: "all products have tag",
  includesNone: "no products have tag",
};

export const checkoutContextOperatorToLabelForProducts: Record<
  CheckoutContextOperator,
  string
> = {
  includesAny: "cart includes any of",
  includesAll: "cart includes all of",
  includesNone: "cart includes none of",
};

/**
 * Predicate types for checkout context conditions.
 * Each type represents a different way to filter/match cart products.
 */
export const checkoutContextPredicateTypes = [
  "collection_membership",
  "product_membership",
  "product_tags",
  "cart_product_count",
  "cart_subtotal",
  "discount_code",
  "discount_percentage",
  "cart_any_item_out_of_stock",
] as const;

export type CheckoutContextPredicateType =
  (typeof checkoutContextPredicateTypes)[number];

export const predicateTypeToLabel: Record<
  CheckoutContextPredicateType,
  string
> = {
  collection_membership: "Collection membership",
  product_membership: "Specific products",
  product_tags: "Product tags",
  cart_product_count: "Total items in cart",
  cart_subtotal: "Cart subtotal",
  discount_code: "Discount code",
  discount_percentage: "Discount percentage",
  cart_any_item_out_of_stock: "Any product in cart is out of stock",
};

// Storefronts each predicate can be evaluated against; absent providers are
// hidden from the branch UI and rejected on save.
export const predicateTypeSupportedProviders: Record<
  CheckoutContextPredicateType,
  readonly Provider[]
> = {
  cart_product_count: [
    Provider.SHOPIFY,
    Provider.BIGCOMMERCE,
    Provider.COMMENTSOLD,
  ],
  cart_subtotal: [Provider.SHOPIFY, Provider.COMMENTSOLD, Provider.BIGCOMMERCE],
  discount_code: [Provider.SHOPIFY, Provider.BIGCOMMERCE],
  discount_percentage: [Provider.SHOPIFY, Provider.BIGCOMMERCE],
  // TODO(bc-tier2): add BIGCOMMERCE once by-id variant lookup and provider-aware
  // id matching land.
  product_membership: [Provider.SHOPIFY],
  // BigCommerce has no native product tag concept.
  product_tags: [Provider.SHOPIFY],
  // TODO(bc-tier3): add BIGCOMMERCE once cart categories are fetched live.
  collection_membership: [Provider.SHOPIFY],
  // TODO(bc-tier3): add BIGCOMMERCE once cart inventory is fetched live.
  cart_any_item_out_of_stock: [Provider.SHOPIFY],
};

export function isPredicateSupportedForProvider(
  predicateType: CheckoutContextPredicateType,
  provider: Provider,
): boolean {
  return predicateTypeSupportedProviders[predicateType].includes(provider);
}

/**
 * Base interface for all checkout context predicates.
 */
interface BaseCheckoutContextPredicate {
  type: "checkout_context_predicate";
  field: keyof Schema["fields"];
  predicateType: CheckoutContextPredicateType;
  operator: CheckoutContextOperator;
}

/**
 * Check if cart products belong to specific Shopify collections.
 */
export interface CollectionMembershipPredicate
  extends BaseCheckoutContextPredicate {
  predicateType: "collection_membership";
  collectionIds: string[];
}

/**
 * Represents a product selection with optional variant filtering.
 * If variantIds is empty or undefined, all variants of the product match.
 */
export interface ProductSelection {
  productId: string;
  productTitle: string;
  imageUrl?: string;
  /** If empty/undefined, all variants match. Otherwise, only these variants match. */
  variantIds?: string[];
  /** Human-readable variant names for display */
  variantTitles?: string[];
}

/**
 * Check if cart contains specific products (optionally specific variants).
 */
export interface ProductMembershipPredicate
  extends BaseCheckoutContextPredicate {
  predicateType: "product_membership";
  /** Products to check for, with optional variant filtering */
  products: ProductSelection[];
}

/**
 * Check if cart products have specific tags.
 */
export interface ProductTagPredicate extends BaseCheckoutContextPredicate {
  predicateType: "product_tags";
  tags: string[];
}

/**
 * Base interface for numeric cart predicates (product count, subtotal).
 * Uses number comparison operators instead of include operators.
 */
interface BaseNumericCartPredicate {
  type: "checkout_context_predicate";
  field: keyof Schema["fields"];
  predicateType: CheckoutContextPredicateType;
  operator: NumberComparisonOperator;
  comparisonValue: number;
}

/**
 * Check if cart product count meets certain criteria.
 * Compares the total quantity of all items in the cart (sum of quantities).
 */
export interface CartProductCountPredicate extends BaseNumericCartPredicate {
  predicateType: "cart_product_count";
}

/**
 * Check if cart subtotal meets certain criteria.
 * Compares the subtotal amount (in store currency) of the cart.
 */
export interface CartSubtotalPredicate extends BaseNumericCartPredicate {
  predicateType: "cart_subtotal";
}

/**
 * Check if any discount code matches specified criteria.
 * Uses text match operators with case-insensitive matching.
 * Reuses the same pattern as TextMatchExpression for consistency.
 */
export interface DiscountCodePredicate {
  type: "checkout_context_predicate";
  field: keyof Schema["fields"];
  predicateType: "discount_code";
  /** Text match operator (startsWith, endsWith, includes, equals) */
  operator: StringMatchOperator;
  /** Values to match against discount code titles (matches if ANY value matches) */
  matchValues: string[];
}

/**
 * Check if the effective discount percentage meets specified criteria.
 * Calculates the effective percentage from cart line item prices and subtotal:
 *   effectivePercentage = ((preDiscountTotal - subtotal) / preDiscountTotal) * 100
 */
export interface DiscountPercentagePredicate {
  type: "checkout_context_predicate";
  field: keyof Schema["fields"];
  predicateType: "discount_percentage";
  operator: NumberComparisonOperator;
  /** The percentage threshold to compare against (e.g., 10 for 10%) */
  comparisonValue: number;
}

/**
 * Check if any cart product variant has less available inventory than the cart requires.
 */
export interface CartAnyItemOutOfStockPredicate {
  type: "checkout_context_predicate";
  field: keyof Schema["fields"];
  predicateType: "cart_any_item_out_of_stock";
}

/**
 * Unified type for all checkout context predicates.
 * This is a discriminated union based on predicateType.
 */
export type CheckoutContextPredicateExpression =
  | CollectionMembershipPredicate
  | ProductMembershipPredicate
  | ProductTagPredicate
  | CartProductCountPredicate
  | CartSubtotalPredicate
  | DiscountCodePredicate
  | DiscountPercentagePredicate
  | CartAnyItemOutOfStockPredicate;

/**
 * Type guard to check if an expression is a checkout context predicate.
 */
export function isCheckoutContextPredicate(
  expression: unknown,
): expression is CheckoutContextPredicateExpression {
  return (
    typeof expression === "object" &&
    expression !== null &&
    "type" in expression &&
    expression.type === "checkout_context_predicate"
  );
}

/**
 * Get the target values from a predicate based on its type.
 * For product_membership, returns product IDs.
 * For numeric predicates (cart_product_count, cart_subtotal, discount_percentage), returns an empty array
 * since they don't have target values.
 * For discount_code, returns the match values.
 */
export function getPredicateTargetValues(
  predicate: CheckoutContextPredicateExpression,
): string[] {
  switch (predicate.predicateType) {
    case "collection_membership":
      return predicate.collectionIds;
    case "product_membership":
      return predicate.products.map((p) => p.productId);
    case "product_tags":
      return predicate.tags;
    case "cart_product_count":
    case "cart_subtotal":
    case "discount_percentage":
    case "cart_any_item_out_of_stock":
      return [];
    case "discount_code":
      return predicate.matchValues ?? [];
  }
}

/**
 * Check if a predicate is a numeric predicate (cart counts or subtotal).
 */
export function isNumericCartPredicate(
  predicate: CheckoutContextPredicateExpression,
): predicate is CartProductCountPredicate | CartSubtotalPredicate {
  return (
    predicate.predicateType === "cart_product_count" ||
    predicate.predicateType === "cart_subtotal"
  );
}

/**
 * Check if a predicate is a discount code predicate.
 */
export function isDiscountCodePredicate(
  predicate: CheckoutContextPredicateExpression,
): predicate is DiscountCodePredicate {
  return predicate.predicateType === "discount_code";
}

/**
 * Check if a predicate is a discount percentage predicate.
 */
export function isDiscountPercentagePredicate(
  predicate: CheckoutContextPredicateExpression,
): predicate is DiscountPercentagePredicate {
  return predicate.predicateType === "discount_percentage";
}

/**
 * Check if a predicate is the cart inventory predicate.
 */
export function isCartAnyItemOutOfStockPredicate(
  predicate: CheckoutContextPredicateExpression,
): predicate is CartAnyItemOutOfStockPredicate {
  return predicate.predicateType === "cart_any_item_out_of_stock";
}
